import { useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Box,
  TextField,
  Typography,
  Button,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { addQuery } from 'src/api/admin';
import Snackbar from '@mui/material/Snackbar';
import Alert from 'src/components/Alert';
import { CONSTANTS, STRINGS } from 'src/commons';
// import { setLocale } from 'yup';
import { loaderReducerAction } from 'src/redux/reducers/loader';
import { useDispatch, useSelector } from 'react-redux';
import PhoneInput from 'react-phone-input-2';
import { getMobileNumber } from 'src/utils/mobileNumberValidator';

// setLocale({
//   mixed: {
//     notType: '${path} is required'
//   }
// });

const AddQuery = () => {
  const dispatch = useDispatch();
  const { startLoading, stopLoading } = loaderReducerAction;
  const { user, gymId } = useSelector((state) => state.common);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
    severity: ''
  });

  const history = useNavigate();

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarState({
      ...snackbarState,
      open: false
    });
  };

  return (
    <>
      <Helmet>
        <title>Add Query | GMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          marginTop: '2rem'
        }}
      >
        <Container maxWidth="sm">
          <Formik
            initialValues={{
              email: '',
              name: '',
              mobileNumber: '',
              gender: 0,
              queryDate: '',
              followUpDate: '',
              reference: '',
              remarks: '',
              profilePic: null
            }}
            validationSchema={Yup.object().shape({
              email: Yup.string()
                .email('Must be a valid email')
                .max(255)
                .required('Email is required'),
              name: Yup.string()
                .min(2)
                .required('Name is required')
                .matches(/^[a-zA-Z]/, 'Name must contains only alphabets.'),
              mobileNumber: Yup.string()
                .required('Mobile Number is Required')
                .min(5, 'Please enter valid mobile number.')
                .max(12),
              gender: Yup.string().required('Gender is required'),
              remarks: Yup.string(),
              reference: Yup.string(),
              queryDate: Yup.date().required('Query date is required'),
              followUpDate: Yup.date().required('Follow Up date is required'),
              callingCode: Yup.string().optional()
            })}
            onSubmit={async (values) => {
              try {
                const formData = new FormData();
                formData.append('full_name', values.name);
                formData.append('email', values.email);
                formData.append('gender', values.gender);
                formData.append('query_date', values.queryDate);
                formData.append('next_query_date', values.followUpDate);
                formData.append('reference', values.reference);
                formData.append('remarks', values.remarks);
                formData.append('gym_id', gymId);

                if (values?.mobileNumber && values?.callingCode) {
                  const mobileNumber = getMobileNumber(
                    values?.mobileNumber,
                    values?.callingCode
                  );
                  formData.append('phone', mobileNumber);
                  formData.append('calling_code', values?.callingCode);
                }

                if (values.profilePic) {
                  formData.append('profile_pic', values.profilePic);
                }
                dispatch(startLoading());
                const res = await addQuery(user, formData);
                dispatch(stopLoading());
                const isError = res.status !== 200;
                const message = isError ? res?.message : "Query Added Successfully"
                setSnackbarState({
                  open: true,
                  message: message,
                  severity: isError ? 'error' : 'success'
                });
                if (!isError) {
                  history(CONSTANTS.ROUTES.PRIVATE_ROUTES.QUERIES);
                }
              } catch (err) {
                setSnackbarState({
                  open: true,
                  message: err.message,
                  severity: 'error'
                });
              }
            }}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
              isSubmitting,
              touched,
              values
            }) => (
              <form onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                  <Typography color="textPrimary" variant="h2">
                    {STRINGS.ADD_QUERY}
                  </Typography>
                </Box>
                <TextField
                  error={Boolean(touched.name && errors.name)}
                  fullWidth
                  helperText={touched.name && errors.name}
                  label="Name"
                  margin="normal"
                  name="name"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.name}
                  variant="outlined"
                />
                <TextField
                  error={Boolean(touched.email && errors.email)}
                  fullWidth
                  helperText={touched.email && errors.email}
                  label="Email Address"
                  margin="normal"
                  name="email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="email"
                  value={values.email}
                  variant="outlined"
                />
                <Box sx={{ marginBottom: '8px' }}>
                  <PhoneInput
                    country={'us'}
                    placeholder="Gym Phone Number"
                    inputStyle={{
                      height: '3.5rem',
                      width: '100%',
                      border:
                        touched.mobileNumber && errors?.mobileNumber
                          ? '1px solid red'
                          : '1px solid rgba(0, 0, 0, 0.23)',
                      marginTop: '16px'
                    }}
                    buttonClass={
                      touched.mobileNumber && errors?.mobileNumber
                        ? 'errorDropdown'
                        : 'flagDropdown'
                    }
                    value={values?.mobileNumber}
                    name="mobileNumber"
                    onChange={(value, country) => {
                      setFieldValue('mobileNumber', value);
                      setFieldValue('callingCode', `+${country?.dialCode}`);
                    }}
                    inputProps={{
                      required: true
                    }}
                    searchPlaceholder="Search"
                    enableSearch
                    countryCodeEditable={false}
                  />
                  {touched.mobileNumber && errors?.mobileNumber && (
                    <p
                      style={{
                        color: 'red',
                        fontFamily: 'Roboto',
                        fontSize: '0.8rem',
                        marginLeft: '15px',
                        marginTop: '5px'
                      }}
                    >
                      {errors?.mobileNumber}
                    </p>
                  )}
                </Box>
                <FormControl
                  variant="outlined"
                  fullWidth
                  margin="normal"
                  id="gender-select"
                >
                  <InputLabel id="gender-label">{STRINGS.GENDER}</InputLabel>
                  <Select
                    labelId="gender-label"
                    id="gender"
                    value={values.gender}
                    onChange={handleChange}
                    label="Gender"
                    name="gender"
                  >
                    <MenuItem name="gender" value={0}>
                      {STRINGS.MALE}
                    </MenuItem>
                    <MenuItem name="gender" value={1}>
                      {STRINGS.FEMALE}
                    </MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  error={Boolean(touched.queryDate && errors.queryDate)}
                  fullWidth
                  helperText={touched.queryDate && errors.queryDate}
                  InputLabelProps={{
                    shrink: true
                  }}
                  label="Query Date"
                  margin="normal"
                  name="queryDate"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="date"
                  value={values.queryDate}
                  variant="outlined"
                />
                <TextField
                  error={Boolean(touched.followUpDate && errors.followUpDate)}
                  fullWidth
                  helperText={touched.followUpDate && errors.followUpDate}
                  InputLabelProps={{
                    shrink: true
                  }}
                  label="Follow Up Date"
                  margin="normal"
                  name="followUpDate"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="date"
                  value={values.followUpDate}
                  variant="outlined"
                />

                <TextField
                  error={Boolean(touched.reference && errors.reference)}
                  fullWidth
                  helperText={touched.reference && errors.reference}
                  label="Reference"
                  margin="normal"
                  name="reference"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.reference}
                  variant="outlined"
                />
                <TextField
                  error={Boolean(touched.remarks && errors.remarks)}
                  fullWidth
                  helperText={touched.remarks && errors.remarks}
                  label="Remarks"
                  margin="normal"
                  name="remarks"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.remarks}
                  variant="outlined"
                />

                <TextField
                  error={Boolean(touched.profilePic && errors.profilePic)}
                  fullWidth
                  helperText={touched.profilePic && errors.profilePic}
                  label="Profile Pic"
                  InputLabelProps={{
                    shrink: true
                  }}
                  margin="normal"
                  id="profilePic"
                  name="profilePic"
                  onBlur={handleBlur}
                  inputProps={{
                    accept: 'image/*'
                  }}
                  onChange={(event) => {
                    setFieldValue('profilePic', event?.currentTarget?.files[0]);
                  }}
                  type="file"
                  variant="outlined"
                />
                <Box sx={{ py: 2 }}>
                  <Button
                    color="primary"
                    disabled={isSubmitting}
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    {STRINGS.ADD_QUERY}
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
          <Snackbar
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={snackbarState.open}
            autoHideDuration={3000}
            onClose={handleClose}
          >
            <div>
              <Alert onClose={handleClose} severity={snackbarState.severity}>
                {snackbarState.message}
              </Alert>
            </div>
          </Snackbar>
        </Container>
      </Box>
    </>
  );
};

export default AddQuery;
